import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { PropertyOptionsDialog } from "./PropertyOptionsDialog";
import {
  useProfileProperties,
  usePropertyOptions,
  useUpdateOption,
  type ProfileProperty,
} from "./api";
import { Authority } from "../../auth/authorities";
import { useAuth } from "../../auth/useAuth";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";
import { usePageTitle } from "../../app/usePageTitle";

const USER_TYPES_URL = "/metas/userTypes";

export function RegistrationPage() {
  usePageTitle("Registration form");
  const { can } = useAuth();
  const notify = useNotify();
  const properties = useProfileProperties();
  const userTypes = usePropertyOptions(USER_TYPES_URL);
  const updateUserType = useUpdateOption(USER_TYPES_URL);
  const [editing, setEditing] = useState<ProfileProperty | null>(null);

  const canEdit = can(Authority.EditRegistrationProperty);

  if (properties.isLoading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
        Registration form
      </Typography>

      <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
        Who can register
      </Typography>
      <Paper sx={{ p: 2, mb: 4 }}>
        <Stack direction="row" sx={{ gap: 3, flexWrap: "wrap" }}>
          {(userTypes.data ?? []).map((type) => (
            <FormControlLabel
              key={type.id}
              label={type.label}
              control={
                <Switch
                  checked={type.status === "ACTIVE"}
                  disabled={!canEdit || updateUserType.isPending}
                  onChange={async (event) => {
                    try {
                      await updateUserType.mutateAsync({
                        ...type,
                        status: event.target.checked ? "ACTIVE" : "INACTIVE",
                      });
                      notify.success(
                        event.target.checked
                          ? `${type.label} can now register`
                          : `${type.label} can no longer register`,
                      );
                    } catch (error) {
                      notify.error(errorMessageFor(error) ?? "Could not update the user type.");
                    }
                  }}
                />
              }
            />
          ))}
        </Stack>
      </Paper>

      <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
        Questions
      </Typography>
      <Paper>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Question</TableCell>
                <TableCell>Mandatory</TableCell>
                <TableCell>Allows &ldquo;other&rdquo;</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(properties.data ?? []).map((property) => (
                <TableRow key={property.id} hover>
                  <TableCell>{property.label}</TableCell>
                  <TableCell>{property.isMandatory ? "Yes" : "No"}</TableCell>
                  <TableCell>{property.otherAllowed ? "Yes" : "No"}</TableCell>
                  <TableCell align="right">
                    {property.apiUrl ? (
                      <Button
                        size="small"
                        aria-label={`Edit options for ${property.label}`}
                        onClick={() => setEditing(property)}
                      >
                        Edit options
                      </Button>
                    ) : (
                      <Chip size="small" label="Free text" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {editing && <PropertyOptionsDialog property={editing} onClose={() => setEditing(null)} />}
    </Box>
  );
}
